import React, { useState, useEffect, useRef } from 'react'
import { Project } from '../../types'
import { formatDistanceToNow } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { useAuth } from '../../contexts/AuthContext'
import { canViewProjectBudget, getBudgetVisibilityText, getBudgetVisibilityIcon } from '../../utils/projectUtils'

interface ProjectCardProps {
  project: Project
  onEdit: (project: Project) => void
  onDelete: (projectId: string) => void
  onViewDetail: (projectId: string) => void
  onStatusChange?: (projectId: string, isActive: boolean) => void
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onEdit,
  onDelete,
  onViewDetail,
  onStatusChange
}) => {
  const [showStatusMenu, setShowStatusMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()
  
  const canViewBudget = canViewProjectBudget(user, project)
  const budgetUsagePercentage = project.budget 
    ? (project.budget.spent / project.budget.amount) * 100 
    : 0

  const getBudgetStatusColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600'
    if (percentage >= 80) return 'text-yellow-600'
    return 'text-green-600'
  }

  const handleStatusChange = async (newStatus: boolean) => {
    if (onStatusChange) {
      await onStatusChange(project.id, newStatus)
    }
    setShowStatusMenu(false)
  }

  // 點擊外部關閉選單
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowStatusMenu(false)
      }
    }

    if (showStatusMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showStatusMenu])


  return (
    <div className="card hover:shadow-lg transition-shadow duration-200 p-6">
      {/* 專案標題區域 */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div 
            className="w-4 h-4 rounded-full flex-shrink-0"
            style={{ backgroundColor: project.color }}
          />
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-secondary-800 truncate">
              {project.name}
            </h3>
            {project.description && (
              <p className="text-sm text-secondary-600 mt-1 line-clamp-2">
                {project.description}
              </p>
            )}
          </div>
        </div>
        
        {/* 狀態切換下拉選單 */}
        <div className="relative flex-shrink-0 ml-2">
          {onStatusChange ? (
            <div className="relative">
              <button
                onClick={() => setShowStatusMenu(!showStatusMenu)}
                className={`px-3 py-1 text-xs rounded-full flex items-center space-x-1 transition-colors ${
                  project.isActive 
                    ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${
                  project.isActive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                }`} />
                <span className="whitespace-nowrap">
                  {project.isActive ? '進行中' : '已停用'}
                </span>
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showStatusMenu && (
                <div ref={menuRef} className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border z-20 min-w-[120px]">
                  <button
                    onClick={() => handleStatusChange(true)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 ${
                      project.isActive ? 'text-green-600 bg-green-50' : 'text-gray-700'
                    }`}
                  >
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                    <span>進行中</span>
                  </button>
                  <button
                    onClick={() => handleStatusChange(false)}
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center space-x-2 ${
                      !project.isActive ? 'text-gray-600 bg-gray-50' : 'text-gray-700'
                    }`}
                  >
                    <span className="w-2 h-2 bg-gray-400 rounded-full" />
                    <span>已停用</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <span className={`px-3 py-1 text-xs rounded-full flex items-center space-x-1 ${
              project.isActive 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                project.isActive ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
              }`} />
              <span className="whitespace-nowrap">
                {project.isActive ? '進行中' : '已停用'}
              </span>
            </span>
          )}
        </div>
      </div>

      {/* 預算資訊 */}
      {project.budget && canViewBudget && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-secondary-600">
                {project.budget.type === 'MONTHLY' ? '月度預算' : '總預算'}
              </span>
              {project.budget.type === 'MONTHLY' && (
                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  本月
                </span>
              )}
              {/* 可見性標示 */}
              <div className="flex items-center space-x-1 text-xs text-secondary-500">
                <span>{getBudgetVisibilityIcon(project)}</span>
                <span>{getBudgetVisibilityText(project)}</span>
              </div>
            </div>
            <span className={`text-sm font-medium ${getBudgetStatusColor(budgetUsagePercentage)}`}>
              {budgetUsagePercentage.toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                budgetUsagePercentage >= 90 ? 'bg-red-500' :
                budgetUsagePercentage >= 80 ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(budgetUsagePercentage, 100)}%` }}
            />
          </div>
          <div className="flex justify-between text-xs text-secondary-500 mt-1">
            <span>已用: ${project.budget.spent.toLocaleString()}</span>
            <span>
              {project.budget.type === 'MONTHLY' ? '本月額度' : '總額'}: 
              ${project.budget.amount.toLocaleString()}
            </span>
          </div>
        </div>
      )}

      {/* 預算隱藏提示 */}
      {project.budget && !canViewBudget && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <span>🔒</span>
            <span>此專案設有預算，但僅限管理層查看</span>
          </div>
        </div>
      )}

      {/* 專案統計 */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-lg font-semibold text-secondary-800">
            {project.members?.length || 0}
          </div>
          <div className="text-xs text-secondary-600">成員</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-semibold text-secondary-800">
            {project._count?.timeEntries || 0}
          </div>
          <div className="text-xs text-secondary-600">工時記錄</div>
        </div>
      </div>

      {/* 建立時間 */}
      <div className="text-xs text-secondary-500 mb-4">
        建立於 {formatDistanceToNow(new Date(project.createdAt), { 
          addSuffix: true, 
          locale: zhTW 
        })}
      </div>

      {/* 操作按鈕 */}
      <div className="flex space-x-2">
        <button
          onClick={() => onViewDetail(project.id)}
          className="flex-1 px-3 py-2 text-sm bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
        >
          查看詳情
        </button>
        <button
          onClick={() => onEdit(project)}
          className="px-3 py-2 text-sm bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 transition-colors"
        >
          編輯
        </button>
        <button
          onClick={() => onDelete(project.id)}
          className="px-3 py-2 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
        >
          刪除
        </button>
      </div>
    </div>
  )
}

export default ProjectCard
